import React, { lazy, useEffect, useCallback, useState, Suspense, memo } from "react"
import { useSelector, useDispatch } from "react-redux"
import { withStyles, makeStyles } from "@material-ui/core/styles"
import Dialog from "@material-ui/core/Dialog"
import MuiDialogTitle from "@material-ui/core/DialogTitle"
import MuiDialogContent from "@material-ui/core/DialogContent"
import IconButton from "@material-ui/core/IconButton"
import CloseIcon from "@material-ui/icons/Close"
import modalRedeploymentStatusHandler from "@/redux/actions/modalRedeploymentAction"
import { resetActivateProcess } from "@/redux/actions/activateProjectProcessAction"
import CornerImg from "@/assets/images/corner-img.png"
import Loading from "@/components/common/Loading"

const styles = (theme) => ({
    root: {
        margin: 0,
        padding: theme.spacing(5),
    },
    closeButton: {
        position: "absolute",
        right: theme.spacing(1),
        top: theme.spacing(1),
        color: "#ffffff",
        backgroundColor: theme.palette.primary.main,
        width: "28px",
        height: "28px",
        "&:hover": {
            backgroundColor: theme.palette.primary.secondary,
        },
    },
})

const DialogTitle = withStyles(styles)((props) => {
    const { children, classes, onClose, ...other } = props
    return (
        <MuiDialogTitle disableTypography className={classes.root} {...other}>
            {onClose ? (
                <IconButton aria-label="close" className={classes.closeButton} onClick={onClose} size="small">
                    <CloseIcon />
                </IconButton>
            ) : null}
        </MuiDialogTitle>
    )
})

const DialogContent = withStyles((theme) => ({
    root: {
        padding: theme.spacing(0),
    },
}))(MuiDialogContent)

const useStyles = makeStyles(() => ({
    dialogContent: {
        zIndex: "1",
    },
    popupCornerImage: {
        position: "absolute",
        top: 0,
        left: 0,
        zIndex: "-1",
        maxWidth: "200px",
    },
}))

const importView = (component) =>
    lazy(() => import(`./project-redeployment-modal/${component}`).catch(() => console.log("import projectRedeploymentCreateModal error")))

const initEmailReminder = {
    reminderType: "Formal",
    reminderFrequency: "Weekly",
    id: 5,
}

const ProjectRedeploymentDialogContainer = memo(() => {
    const classes = useStyles()
    const dispatch = useDispatch()
    const [modals] = useState(() => [
        {
            step: 1,
            component: "ProjectDetail",
            id: "project-detail-modal",
        },
        {
            step: 2,
            component: "AddSponsor",
            id: "add-sponsor-modal",
        },
        {
            step: 3,
            component: "ViewParticipant",
            id: "view-participants-modal",
        },
        {
            step: 4,
            component: "EmailNotification",
            id: "email-notification-modal",
        },
        {
            step: 5,
            component: "FinalConfirmation",
            id: "final-confirmation-modal",
        },
        {
            step: 6,
            component: "ProcessingReview",
            id: "processing-review",
        },
    ])
    const [views, setViews] = useState()

    const { modalRedeploymentProject, modalRedeploymentShow } = useSelector((state) => state.modalRedeploymentReducer)

    const { activeProducts } = useSelector((state) => state.productReducer)

    const initContainerDetail = {
        step: 1,
        newProject: null,
        projectSponsor: null,
        emailReminder: initEmailReminder,
        participantList: [],
    }

    const [initProjectSponsor, setInitProjectSponsor] = useState(null)
    const [containerDetail, setContainerDetail] = useState(initContainerDetail)

    const renderTargetInit = () => {
        const redeploymentProjectProductFiltered = modalRedeploymentProject
            ? activeProducts.filter((product) => product.productId === modalRedeploymentProject.productId)
            : null

        const redeploymentProjectProductFileName =
            redeploymentProjectProductFiltered &&
            `${redeploymentProjectProductFiltered[0].productName.trim().toLowerCase().replace(/\s/g, "-")}-icon`

        const path = redeploymentProjectProductFileName && `assets/images/product-images/${redeploymentProjectProductFileName}`
        const redeploymentProjectProductFilteredItem = redeploymentProjectProductFiltered && redeploymentProjectProductFiltered[0]
        const redeployProjectInit = {
            companyId: redeploymentProjectProductFilteredItem && redeploymentProjectProductFilteredItem.companyId,
            skus: redeploymentProjectProductFilteredItem && redeploymentProjectProductFilteredItem.skus,
            orderId: redeploymentProjectProductFilteredItem && redeploymentProjectProductFilteredItem.orderId,
            subscriptionStartDate: redeploymentProjectProductFilteredItem && redeploymentProjectProductFilteredItem.subscriptionStartDate,
            subscriptionId: redeploymentProjectProductFilteredItem && redeploymentProjectProductFilteredItem.subscriptionId,
            subscriptionEndDate: redeploymentProjectProductFilteredItem && redeploymentProjectProductFilteredItem.subscriptionEndDate,
            participantMaxCount: redeploymentProjectProductFilteredItem && redeploymentProjectProductFilteredItem.participantMaxCount,
            industryId: modalRedeploymentProject && modalRedeploymentProject.industryId,
            productId: redeploymentProjectProductFilteredItem && redeploymentProjectProductFilteredItem.productId,
            productName: redeploymentProjectProductFilteredItem && redeploymentProjectProductFilteredItem.productName,
            projectReminder: redeploymentProjectProductFilteredItem && redeploymentProjectProductFilteredItem.projectReminder,
            projectSponsor: modalRedeploymentProject && modalRedeploymentProject.projectSponsor,
            productImagePath: path,
            projectTargetId: modalRedeploymentProject && modalRedeploymentProject.projectId,
        }
        const sponsorFirstName =
            modalRedeploymentProject && modalRedeploymentProject.projectSponsor && modalRedeploymentProject.projectSponsor.split(" ")[0]

        const sponsorLastName =
            modalRedeploymentProject && modalRedeploymentProject.projectSponsor && modalRedeploymentProject.projectSponsor.split(" ")[1]

        const initProjectSponsorValue = {
            sponsorFirstName,
            sponsorLastName,
            sponsorEmail: modalRedeploymentProject && modalRedeploymentProject.projectSponsorEmail,
            confirm: false,
            newUser: false,
        }

        setInitProjectSponsor(initProjectSponsorValue)
        setContainerDetail({
            ...containerDetail,
            newProject: redeployProjectInit,
            projectSponsor: initProjectSponsorValue,
        })
    }

    useEffect(() => {
        if (modalRedeploymentProject && !containerDetail.newProject) {
            renderTargetInit()
        }
    }, [modalRedeploymentProject])

    const { step, newProject, projectSponsor, participantList, emailReminder } = containerDetail

    const prevStep = useCallback(() => {
        setContainerDetail((prev) => ({ ...prev, step: prev.step - 1 }))
    }, [step])

    const nextStep = useCallback(() => {
        setContainerDetail((prev) => ({ ...prev, step: prev.step + 1 }))
    }, [step])

    const handleSetNewProject = useCallback(
        (project) => {
            setContainerDetail((prev) => ({ ...prev, newProject: project }))
        },
        [newProject]
    )

    const handleSetProjectSponsor = useCallback(
        (sponsor) => {
            setContainerDetail((prev) => ({ ...prev, projectSponsor: sponsor }))
        },
        [projectSponsor]
    )

    const handleSetParticipantListMain = useCallback(
        (participants) => {
            setContainerDetail((prev) => ({ ...prev, participantList: participants }))
        },
        [participantList]
    )

    const handleEmailReminder = useCallback(
        (emailReminderSetting) => {
            setContainerDetail((prev) => ({ ...prev, emailReminder: emailReminderSetting }))
        },
        [emailReminder]
    )

    const handleClose = () => {
        // reset everything
        dispatch(
            modalRedeploymentStatusHandler({
                modalRedeploymentProjectId: null,
                currentStep: 1,
                modalRedeploymentShow: false,
            })
        )
        setContainerDetail(initContainerDetail)
        dispatch(resetActivateProcess)
    }

    const loadViews = async () => {
        const modalToShow = modals.find((modal) => {
            if (step === modal.step) {
                return modal
            }
            return null
        })
        const componentPromises = async () => {
            const View = await importView(modalToShow.component)
            return modalRedeploymentProject ? (
                <View
                    nextStep={nextStep}
                    handleClose={handleClose}
                    prevStep={prevStep}
                    handleSetNewProject={handleSetNewProject}
                    handleSetProjectSponsor={handleSetProjectSponsor}
                    handleSetParticipantListMain={handleSetParticipantListMain}
                    handleEmailReminder={handleEmailReminder}
                    initProjectSponsor={initProjectSponsor}
                    initEmailReminder={initEmailReminder}
                    containerDetail={containerDetail}
                />
            ) : (
                <div>null</div>
            )
        }
        const view = await componentPromises()
        setViews(view)
    }

    useEffect(() => {
        loadViews()
    }, [step, containerDetail, modalRedeploymentProject])

    return (
        <>
            {modalRedeploymentProject ? (
                <Suspense fallback={<Loading />}>
                    <Dialog aria-labelledby="redeployment" open={modalRedeploymentShow} maxWidth="md" fullWidth>
                        <DialogTitle className={classes.closeButton} id="redeployment" onClose={handleClose} />
                        <DialogContent className={classes.dialogContent}>
                            <img src={CornerImg} alt="popup-corner" className={classes.popupCornerImage} />
                            {views}
                        </DialogContent>
                    </Dialog>
                </Suspense>
            ) : null}
        </>
    )
})

export default ProjectRedeploymentDialogContainer
